import { StarIcon } from "@heroicons/react/24/solid";
import { Home, Users } from "lucide-react";
import StayCard from "components/StayCard/StayCard";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "redux/store";
import { getExclusiveProperty } from "redux/reducers/Properties/PropertiesSlice";
import { resetSearch } from "redux/reducers/Search/SearchSlice";

export default function HotelBooking() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { exclusiveProperty, loading } = useSelector(
    (state: RootState) => state.property
  );

  useEffect(() => {
    dispatch(getExclusiveProperty());
  }, [dispatch]);

const handleBrowse = () => {
  dispatch(resetSearch());
  navigate("/properties"); // ← change from "/listing-stay-map"
};

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-900">

      {/* Hero */}
      <header className="container mx-auto px-4 pt-14 pb-0 lg:pt-24">
        <div className="max-w-2xl">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#f5694b] mb-5">
            Lima, Peru · Student Housing
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-[1.1] mb-5">
            Your home<br />away from home
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 leading-relaxed mb-8 max-w-lg">
            Premium rooms in Lima's most vibrant neighborhoods — handpicked for students and young professionals.
          </p>
          <button
            onClick={handleBrowse}
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#f5694b] hover:bg-[#e2583d] text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-sm"
          >
            Browse Properties
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-4 mt-12 pb-12 border-b border-gray-100 dark:border-neutral-800">
          {[
            { icon: Home, value: "15+", label: "Properties" },
            { icon: Users, value: "500+", label: "Happy students" },
            { icon: StarIcon, value: "4.9★", label: "Average rating" },
          ].map(({ icon: Icon, value, label }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#f5694b]/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-[#f5694b]" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900 dark:text-white leading-none">{value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{label}</p>
              </div>
            </div>
          ))}
        </div>
      </header>

      {/* Properties */}
      <section className="py-14 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-[#f5694b] mb-1">Exclusive</p>
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                Our properties in Lima
              </h2>
            </div>
            <button
              onClick={handleBrowse}
              className="hidden sm:inline-flex text-sm font-semibold text-[#f5694b] hover:text-[#e2583d] transition-colors items-center gap-1"
            >
              View all
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#f5694b] border-t-transparent" />
            </div>
          ) : Array.isArray(exclusiveProperty) && exclusiveProperty.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {exclusiveProperty.map((item) => (
                  <div key={item.id} className="transform transition-all duration-300 hover:-translate-y-1">
                    <StayCard data={item} />
                  </div>
                ))}
              </div>
              <div className="text-center mt-10 sm:hidden">
                <button
                  onClick={handleBrowse}
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#f5694b] hover:bg-[#e2583d] text-white font-semibold rounded-full shadow-lg text-sm"
                >
                  View all properties
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-20 bg-gray-50 dark:bg-neutral-800 rounded-2xl">
              <Home className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-400">No properties available at the moment.</p>
            </div>
          )}
        </div>
      </section>

    </div>
  );
}